import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FiEye, FiEyeOff, FiCheckCircle } from "react-icons/fi"

import { editCourseDetails } from "../../../../../services/operations/courseDetailsAPI"
import { resetCourseState, setStep } from "../../../../../slices/courseSlice"
import { COURSE_STATUS } from "../../../../../utils/constants"
import IconBtn from "../../../../common/IconBtn"

export default function PublishCourse() {
  const { register, handleSubmit, setValue, getValues } = useForm()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { token } = useSelector((state) => state.auth)
  const { course } = useSelector((state) => state.course)
  const [loading, setLoading] = useState(false)
  const [isPublic, setIsPublic] = useState(false)

  useEffect(() => {
    const pub = course?.status === COURSE_STATUS.PUBLISHED
    setIsPublic(pub)
    setValue("public", pub)
  }, [])

  const goToCourses = () => {
    dispatch(resetCourseState())
    navigate("/dashboard/my-courses")
  }

  const handleCoursePublish = async () => {
    const willBePublic = getValues("public")
    const alreadyMatches =
      (course?.status === COURSE_STATUS.PUBLISHED && willBePublic) ||
      (course?.status === COURSE_STATUS.DRAFT && !willBePublic)

    if (!alreadyMatches) {
      const fd = new FormData()
      fd.append("courseId", course._id)
      fd.append("status", willBePublic ? COURSE_STATUS.PUBLISHED : COURSE_STATUS.DRAFT)
      setLoading(true)
      const result = await editCourseDetails(fd, token)
      setLoading(false)
      if (!result) return
    }

    dispatch(setStep(4))
  }

  return (
    <div className="space-y-5 rounded-2xl border border-richblack-700 bg-richblack-800 p-4 sm:p-6">
      <div>
        <h2 className="text-lg font-semibold text-richblack-5 sm:text-xl">Publish Settings</h2>
        <p className="mt-1 text-sm text-richblack-400">
          Choose whether to publish immediately or save as a draft.
        </p>
      </div>

      <form onSubmit={handleSubmit(handleCoursePublish)} className="space-y-5">
        {/* Toggle card */}
        <div
          onClick={() => {
            const next = !isPublic
            setIsPublic(next)
            setValue("public", next)
          }}
          className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 p-4 transition-all sm:gap-4 sm:p-5 ${
            isPublic
              ? "border-caribbeangreen-300/60 bg-caribbeangreen-300/5"
              : "border-richblack-600 bg-richblack-700/50 hover:border-richblack-500"
          }`}
        >
          <div className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-full ${
            isPublic ? "bg-caribbeangreen-300/20 text-caribbeangreen-300" : "bg-richblack-600 text-richblack-400"
          }`}>
            {isPublic ? <FiEye size={18} /> : <FiEyeOff size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isPublic ? "text-caribbeangreen-300" : "text-richblack-5"}`}>
              {isPublic ? "Published — Visible to students" : "Draft — Hidden from students"}
            </p>
            <p className="mt-0.5 text-xs text-richblack-400">
              {isPublic
                ? "Students can find and enroll in your course."
                : "Only you can see this. Publish when it's ready."}
            </p>
          </div>
          {isPublic && <FiCheckCircle className="flex-shrink-0 text-caribbeangreen-300" size={18} />}
          <input type="checkbox" id="public" {...register("public")} className="sr-only" />
        </div>

        {/* Summary grid */}
        <div className="rounded-xl bg-richblack-700/50 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-richblack-400">
            Course Summary
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-4">
            {[
              { label: "Title", value: course?.courseName || "—", wide: true },
              { label: "Sections", value: course?.courseContent?.length ?? 0 },
              { label: "Price", value: course?.price ? `₹${course.price.toLocaleString("en-IN")}` : "Free" },
              { label: "Lectures", value: course?.courseContent?.reduce((n, s) => n + s.subSection.length, 0) ?? 0 },
            ].map((item) => (
              <div key={item.label} className={item.wide ? "col-span-2" : ""}>
                <p className="text-xs text-richblack-400">{item.label}</p>
                <p className="truncate font-medium text-richblack-5">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Actions — stack on mobile */}
        <div className="flex flex-col gap-3 border-t border-richblack-700 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            disabled={loading}
            onClick={() => dispatch(setStep(2))}
            className="order-last w-full rounded-xl border border-richblack-600 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 disabled:opacity-50 sm:order-first sm:w-auto sm:px-5"
          >
            ← Back
          </button>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <button
              type="button"
              disabled={loading}
              onClick={goToCourses}
              className="w-full rounded-xl border border-richblack-600 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 disabled:opacity-50 sm:w-auto sm:px-5"
            >
              Save as Draft
            </button>
            <IconBtn
              disabled={loading}
              text={loading ? "Saving…" : "Next: Assessment →"}
            />
          </div>
        </div>
      </form>
    </div>
  )
}
